import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';

// StarterKit v3 bundles @tiptap/extension-link — registering a standalone Link on top of it
// warns "Duplicate extension names found: ['link']", so configure StarterKit's bundled one.
const buildExtensions = (placeholder: string | undefined) => [
  StarterKit.configure({ link: { openOnClick: false, autolink: true } }),
  Markdown.configure({ html: false, linkify: true, transformPastedText: true }),
  ...(placeholder ? [Placeholder.configure({ placeholder })] : [])
];

interface CommentEditorProps {
  markdown: string;
  placeholder?: string;
  autoFocus?: boolean;
  // Same disable signal as the parent's submit button — Enter also gates on this so a
  // whitespace-only draft can't slip through the keyboard shortcut. Defaults to true so
  // read-only callers (no onSubmit) don't have to opt in.
  canSubmit?: boolean;
  onChange: (markdown: string) => void;
  // Enter (no shift) fires onSubmit when canSubmit; Shift+Enter still inserts a newline.
  // Esc fires onCancel.
  onSubmit?: () => void;
  onCancel?: () => void;
}

export const CommentEditor = ({
  markdown,
  placeholder,
  autoFocus = false,
  canSubmit = true,
  onChange,
  onSubmit,
  onCancel
}: CommentEditorProps): ReactNode => {
  // Tracks the last value we pushed to the parent via onChange. Comparing against this
  // (rather than re-serializing the editor's current markdown) closes a caret-drop bug:
  // getMarkdown() normalizes list bullets / trailing newlines / escapes, so a parent-driven
  // reset to a byte-different-but-semantically-identical value would re-trigger setContent
  // and reset the cursor. The ref reflects what the parent has seen, not what the editor
  // would emit if asked.
  const lastMarkdownPushedToParentRef = useRef(markdown);
  const extensions = useMemo(() => buildExtensions(placeholder), [placeholder]);
  const editor = useEditor({
    extensions,
    content: markdown,
    editorProps: {
      attributes: {
        // `.markdown-body` on the editor keeps the write-side prose styles in sync with the
        // read-side <CommentBody> — what you type is what you see.
        class: 'activity-feed-detail-comment-editor-input markdown-body'
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Escape' && onCancel) {
          event.preventDefault();
          onCancel();
          return true;
        }
        if (event.key === 'Enter' && !event.shiftKey && onSubmit && canSubmit) {
          event.preventDefault();
          onSubmit();
          return true;
        }
        return false;
      }
    },
    onUpdate: ({ editor: e }) => {
      const md = e.storage.markdown.getMarkdown();
      lastMarkdownPushedToParentRef.current = md;
      onChange(md);
    }
  });

  // TipTap's `content` option only seeds the initial value; external resets (parent setting
  // markdown='') require explicit setContent.
  useEffect(() => {
    if (!editor) return;
    if (markdown !== lastMarkdownPushedToParentRef.current) {
      lastMarkdownPushedToParentRef.current = markdown;
      editor.commands.setContent(markdown);
    }
  }, [editor, markdown]);

  useLayoutEffect(() => {
    if (autoFocus && editor) {
      editor.commands.focus('end');
    }
  }, [autoFocus, editor]);

  if (!editor) return null;

  return <EditorContent editor={editor} />;
};
